/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * productSpecFields — the canonical list of the FIXED product-specification
 * columns (modeled on the source app's "Product Specs" workbook, Specs_v2 tab).
 * This single source of truth drives:
 *   - the spec editor modal (shared/components/ProductSpecModal)
 *   - the product-specifications grid + branded export
 *   - the productSpecsService write whitelist (column == field key)
 *
 * Each field: { key, label, type, unit? }  where key === the product_specs DB
 * column.
 *   type 'text'   → free text (codes/UPCs keep leading zeros — never numeric)
 *   type 'number' → numeric measurement
 *   type 'int'    → whole-number count
 *   unit 'weight'|'length' → a dimensional value whose abbreviation follows the
 *     sheet-wide unit system (US: lbs/in, metric: kg/cm). The label is stored
 *     WITHOUT the unit; call `fieldLabel(field, system)` to render it with the
 *     active abbreviation appended.
 * Grouped for a tidy modal/section layout.
 */

export const SPEC_GROUPS = [
  {
    title: "Identity",
    fields: [
      { key: "sku_code", label: "SKU", type: "text" },
      { key: "brand_name", label: "Brand Name", type: "text" },
    ],
  },
  {
    title: "General & Regulatory",
    fields: [
      { key: "abv", label: "ABV (%)", type: "number" },
      { key: "ttb_cola", label: "TTB ID / COLA", type: "text" },
      { key: "products_per_case", label: "Products per Case", type: "int" },
      { key: "product_upc", label: "Product UPC", type: "text" },
      { key: "shipping_case_upc", label: "Shipping Case UPC", type: "text" },
      { key: "shelf_life", label: "Shelf Life (Mo)", type: "text" },
      { key: "lot_code_format", label: "Lot Code Format", type: "text" },
      { key: "lot_code_example", label: "Lot Code Example", type: "text" },
    ],
  },
  {
    title: "Product Dimensions",
    fields: [
      { key: "product_weight_lbs", label: "Product Weight", type: "number", unit: "weight" },
      { key: "product_length_in", label: "Product Length", type: "number", unit: "length" },
      { key: "product_width_in", label: "Product Width", type: "number", unit: "length" },
      { key: "product_height_in", label: "Product Height", type: "number", unit: "length" },
    ],
  },
  {
    title: "Shipping Case",
    fields: [
      { key: "case_weight_lbs", label: "Shipping Case Weight", type: "number", unit: "weight" },
      { key: "case_length_in", label: "Shipping Case Length", type: "number", unit: "length" },
      { key: "case_width_in", label: "Shipping Case Width", type: "number", unit: "length" },
      { key: "case_height_in", label: "Shipping Case Height", type: "number", unit: "length" },
    ],
  },
  {
    title: "Pallet",
    fields: [
      { key: "cases_per_layer", label: "Cases / Layer", type: "int" },
      { key: "layers_per_pallet", label: "Layers / Pallet", type: "int" },
      { key: "cases_per_pallet", label: "Cases / Pallet", type: "int" },
      { key: "pallet_weight_lbs", label: "Pallet Weight", type: "number", unit: "weight" },
      { key: "pallet_length_in", label: "Pallet Length", type: "number", unit: "length" },
      { key: "pallet_width_in", label: "Pallet Width", type: "number", unit: "length" },
      { key: "pallet_height_in", label: "Pallet Height", type: "number", unit: "length" },
    ],
  },
];

// Flat list of every fixed spec field (excludes the SKU name, which lives on
// products, and excludes user-defined custom columns).
export const SPEC_FIELDS = SPEC_GROUPS.flatMap((g) => g.fields);

// Just the field keys — used as the productSpecs write whitelist.
export const SPEC_FIELD_KEYS = SPEC_FIELDS.map((f) => f.key);

// Suggested image tags for the tagged-photo uploader (free text is allowed too).
export const IMAGE_TAGS = ["front", "back", "side", "top", "label", "case", "other"];

// ── Unit system (sheet-wide; stored on accounts.spec_unit_system) ─────────────
export const UNIT_SYSTEMS = ["us", "metric"];
const UNIT_ABBR = {
  weight: { us: "lbs", metric: "kg" },
  length: { us: "in", metric: "cm" },
};

/** The abbreviation for a field's unit under the active system ("" if none). */
export function unitAbbr(unit, system = "us") {
  if (!unit) return "";
  const sys = system === "metric" ? "metric" : "us";
  return UNIT_ABBR[unit]?.[sys] ?? "";
}

/** Render label with the active unit appended, e.g. "Product Weight (kg)". */
export function fieldLabel(field, system = "us") {
  const abbr = unitAbbr(field?.unit, system);
  return abbr ? `${field.label} (${abbr})` : field.label;
}

// ── Reserved tags for the dedicated UPC photo slots ───────────────────────────
// These images are managed inline next to the UPC number fields (not in the
// general photo gallery) and are embedded in the export beside the number.
export const UPC_PHOTO_TAGS = {
  product_upc: "product-upc",
  shipping_case_upc: "shipping-case-upc",
};
export const UPC_TAG_SET = new Set(Object.values(UPC_PHOTO_TAGS));
